import React from "react";

import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { Box, BoxProps, Button, Paper } from "@portex-pro/ui-components";
import { ContractPricingFrequency } from "api/rest/rfp/brokerBidContractApi/types/ContractPricingFrequency";
import SingleCalendarPicker from "components/SingleCalendarPicker";
import Text from "components/Text";
import ThrottledTextInput from "components/ThrottledTextInput";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { ModeRfp } from "types/Mode";
import OptionalMaybe from "types/OptionalMaybe";

import PricingFrequencyView from "../../bid-attributes/components/PricingFrequencyView";

type CreateNewBidViewProps = {
  boxProps?: BoxProps;
  bidDeadline?: string;
  bidTitle: string;
  mode: ModeRfp;
  pricingFrequency: OptionalMaybe<ContractPricingFrequency>;
  contractStartDate?: string;
  contractEndDate?: string;
  enableRfpBulkSpotQuote?: boolean;
  onBidDeadlineChange: (val: MaterialUiPickersDate) => void;
  onBidTitleChange: (val: string) => void;
  onPricingFrequencyChange: (val: ContractPricingFrequency) => void;
  onContractStartDateChange: (val: MaterialUiPickersDate) => void;
  onContractEndDateChange: (val: MaterialUiPickersDate) => void;
  onSubmit: () => void;
};

const CreateNewBidView: React.FC<CreateNewBidViewProps> = ({
  boxProps,
  bidDeadline,
  bidTitle,
  mode,
  pricingFrequency,
  contractStartDate,
  contractEndDate,
  enableRfpBulkSpotQuote = false,
  onBidDeadlineChange,
  onBidTitleChange,
  onPricingFrequencyChange,
  onContractStartDateChange,
  onContractEndDateChange,
  onSubmit,
}) => {
  const { t } = useTranslation("shipper", { keyPrefix: "bids.pageCreateBid" });

  const isSpotBulkQuote = pricingFrequency === ContractPricingFrequency.BULK_SPOT_QUOTE;
  const shouldShowContractDates = pricingFrequency && !isSpotBulkQuote;
  const shouldShowBidDeadline = !isSpotBulkQuote;

  const isFormValid =
    bidTitle && pricingFrequency && (isSpotBulkQuote || (bidDeadline && contractStartDate && contractEndDate));

  return (
    <Box {...boxProps}>
      <Paper className="p-4 shadow-lg rounded-lg">
        <div className="grid grid-cols-3 gap-4 mb-4 w-full">
          <div>
            <Text size="medium" weight="bold" required>
              {t("pricingFrequency")}
            </Text>
            <PricingFrequencyView
              value={pricingFrequency}
              onChange={onPricingFrequencyChange}
              enableRfpBulkSpotQuote={enableRfpBulkSpotQuote}
              mode={mode}
              placeholder={t("selectPlaceholder")}
            />
          </div>
          {shouldShowContractDates && (
            <>
              <div>
                <Text size="medium" weight="bold" required>
                  {t("contractStartDate")}
                </Text>
                <SingleCalendarPicker
                  value={contractStartDate ? DateTime.fromISO(contractStartDate) : null}
                  onChange={onContractStartDateChange}
                  margin="normal"
                  startIconPallete="grey"
                />
              </div>
              <div>
                <Text size="medium" weight="bold" required>
                  {t("contractEndDate")}
                </Text>
                <SingleCalendarPicker
                  value={contractEndDate ? DateTime.fromISO(contractEndDate) : null}
                  onChange={onContractEndDateChange}
                  margin="normal"
                  startIconPallete="grey"
                />
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-3 gap-4 items-end">
          <div>
            <Text size="medium" weight="bold" required>
              {t("bidTitle")}
            </Text>
            <ThrottledTextInput
              placeholder={t("bidTitlePlaceholder")}
              value={bidTitle}
              onChange={onBidTitleChange}
              margin="normal"
            />
          </div>

          {shouldShowBidDeadline && (
            <div>
              <Text size="medium" weight="bold" required>
                {t("bidResponseDeadline")}
              </Text>
              <SingleCalendarPicker
                value={bidDeadline ? DateTime.fromISO(bidDeadline) : null}
                onChange={onBidDeadlineChange}
                placeholder={t("selectPlaceholder")}
                margin="normal"
                startIconPallete="grey"
              />
            </div>
          )}

          <div className="mb-2">
            <Button
              variant="contained"
              color="primary"
              onClick={onSubmit}
              disabled={!isFormValid}
              className="h-[42px] bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors duration-200"
            >
              {t("createNewBidButton")}
            </Button>
          </div>
        </div>
      </Paper>
    </Box>
  );
};

export default CreateNewBidView;
